import { useMemo, useState } from 'react';
import {
  X, ExternalLink, ChevronDown, ChevronUp, Plus, Check, MapPin,
} from 'lucide-react';
import { Button, Badge } from './ui';
import {
  SCHOOL_TYPE_LABELS,
  OWNERSHIP_LABELS,
  TEST_REQUIREMENT_LABELS,
  localeBucket,
  cipFamily,
  cipFamilyLabel,
} from '../lib/schoolDiscoveryTypes';
import type { DiscoverySchool, DiscoveryMajor } from '../lib/schoolDiscoveryTypes';

// =============================================================================
// SchoolProfile (v0.2.32)
//
// Full profile for one discovery school: criteria, costs, outcomes, student
// body, and a collapsible "Programs offered" list grouped by 2-digit CIP
// family. "Add as target school" writes into the applicant's target_schools.
//
// Every value uses honest "not reported" rendering — College Scorecard data
// is patchy (open-enrollment schools have no admit rate, etc.), so a missing
// value is shown as "Not reported", never hidden or guessed.
// =============================================================================

function pct(v: number | null): string {
  return v == null ? 'Not reported' : `${Math.round(v * 100)}%`;
}
function money(v: number | null): string {
  return v == null ? 'Not reported' : '$' + v.toLocaleString();
}
function num(v: number | null): string {
  return v == null ? 'Not reported' : v.toLocaleString();
}

function Stat({ label, value }: { label: string; value: string }) {
  const missing = value === 'Not reported';
  return (
    <div>
      <dt className="text-xs text-soft">{label}</dt>
      <dd className={`text-sm ${missing ? 'text-soft italic' : 'text-ink'}`}>
        {value}
      </dd>
    </div>
  );
}

interface Props {
  school: DiscoverySchool;
  /** null = not a target yet; string = already a target (its school_id). */
  alreadyTargetId: string | null;
  adding: boolean;
  onAddAsTarget: () => void;
  onClose: () => void;
}

export function SchoolProfile({
  school: s, alreadyTargetId, adding, onAddAsTarget, onClose,
}: Props) {
  const [programsOpen, setProgramsOpen] = useState(false);

  // Programs grouped by 2-digit CIP family.
  const programGroups = useMemo(() => {
    const groups = new Map<string, DiscoveryMajor[]>();
    for (const m of s.majors) {
      const fam = cipFamily(m.code);
      if (!groups.has(fam)) groups.set(fam, []);
      groups.get(fam)!.push(m);
    }
    return [...groups.entries()]
      .map(([fam, majors]) => ({
        fam,
        label: cipFamilyLabel(fam),
        majors: majors.slice().sort((a, b) => a.title.localeCompare(b.title)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [s.majors]);

  const setting = localeBucket(s.locale);
  const satRange =
    s.satReading25 != null && s.satMath25 != null &&
    s.satReading75 != null && s.satMath75 != null
      ? `${s.satReading25 + s.satMath25}–${s.satReading75 + s.satMath75}`
      : s.satAvg != null ? `avg ${s.satAvg}` : 'Not reported';
  const actRange =
    s.act25 != null && s.act75 != null ? `${s.act25}–${s.act75}` : 'Not reported';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">{s.name}</h2>
          <p className="text-sm text-soft flex items-center gap-1">
            <MapPin size={12} />
            {[s.city, s.state].filter(Boolean).join(', ') || 'Location not reported'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-soft hover:text-soft"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5">
        {s.degreePredominant != null && SCHOOL_TYPE_LABELS[s.degreePredominant] && (
          <Badge variant="indigo">{SCHOOL_TYPE_LABELS[s.degreePredominant]}</Badge>
        )}
        {s.ownership != null && OWNERSHIP_LABELS[s.ownership] && (
          <Badge variant="gray">{OWNERSHIP_LABELS[s.ownership]}</Badge>
        )}
        {setting && <Badge variant="gray">{setting}</Badge>}
        {s.hsi === 1 && <Badge variant="green">Hispanic-Serving Institution</Badge>}
        {s.religious != null && <Badge variant="gray">Religious affiliation</Badge>}
        {s.onlineOnly === 1 && <Badge variant="gray">Online only</Badge>}
        {s.openAdmissions === 1 && <Badge variant="gray">Open admissions</Badge>}
      </div>

      {/* Add as target */}
      <div>
        {alreadyTargetId ? (
          <div className="flex items-center gap-1.5 text-sm text-green-700">
            <Check size={15} /> Already one of this applicant's target schools.
          </div>
        ) : (
          <Button size="sm" onClick={onAddAsTarget} disabled={adding}>
            <Plus size={14} className="mr-1" />
            {adding ? 'Adding…' : 'Add as target school'}
          </Button>
        )}
      </div>

      {/* Admissions */}
      <section>
        <h3 className="text-sm font-medium text-ink mb-1.5">Admissions</h3>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Admission rate" value={pct(s.admitRate)} />
          <Stat label="SAT range" value={satRange} />
          <Stat label="ACT range" value={actRange} />
          <Stat
            label="Test policy"
            value={s.testRequirements != null
              ? (TEST_REQUIREMENT_LABELS[s.testRequirements] ?? 'Not reported')
              : 'Not reported'}
          />
        </dl>
      </section>

      {/* Cost */}
      <section>
        <h3 className="text-sm font-medium text-ink mb-1.5">Cost</h3>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="In-state tuition" value={money(s.tuitionIn)} />
          <Stat label="Out-of-state tuition" value={money(s.tuitionOut)} />
          <Stat label="Room & board" value={money(s.roomBoard)} />
          <Stat label="Avg. net price" value={money(s.netPrice)} />
        </dl>
        {s.netPriceCalcUrl && (
          <a
            href={s.netPriceCalcUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-forest-700 hover:underline"
          >
            <ExternalLink size={11} /> Net price calculator
          </a>
        )}
      </section>

      {/* Outcomes & size */}
      <section>
        <h3 className="text-sm font-medium text-ink mb-1.5">Outcomes &amp; size</h3>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Graduation rate" value={pct(s.gradRate)} />
          <Stat label="First-year retention" value={pct(s.retentionRate)} />
          <Stat label="Median earnings (10 yr)" value={money(s.earnings10yr)} />
          <Stat label="Undergrad enrollment" value={num(s.size)} />
          <Stat label="Student–faculty ratio"
            value={s.studentFacultyRatio != null ? `${s.studentFacultyRatio}:1` : 'Not reported'} />
          <Stat label="Part-time share" value={pct(s.partTimeShare)} />
        </dl>
      </section>

      {/* Student body */}
      <section>
        <h3 className="text-sm font-medium text-ink mb-1.5">Student body</h3>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Women" value={pct(s.pctWomen)} />
          <Stat label="Men" value={pct(s.pctMen)} />
          <Stat label="Hispanic" value={pct(s.pctHispanic)} />
          <Stat label="White" value={pct(s.pctWhite)} />
          <Stat label="Black" value={pct(s.pctBlack)} />
          <Stat label="Asian" value={pct(s.pctAsian)} />
          <Stat label="Two or more races" value={pct(s.pctTwoOrMore)} />
          <Stat label="Non-resident" value={pct(s.pctNonResident)} />
        </dl>
      </section>

      {/* Programs offered */}
      <section>
        <button
          type="button"
          onClick={() => setProgramsOpen((o) => !o)}
          className="flex items-center gap-1 text-sm font-medium text-ink"
        >
          {programsOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          Programs offered ({s.majors.length})
        </button>
        {programsOpen && (
          <div className="mt-2 space-y-2">
            {programGroups.length === 0 ? (
              <p className="text-sm text-soft italic">No program data reported.</p>
            ) : (
              programGroups.map((g) => (
                <div key={g.fam}>
                  <p className="text-xs font-medium text-soft">{g.label}</p>
                  <ul className="ml-3 list-disc text-sm text-soft">
                    {g.majors.map((m) => (
                      <li key={m.code}>{m.title.replace(/\.$/, '')}</li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {s.url && (
        <a
          href={s.url.startsWith('http') ? s.url : `https://${s.url}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-forest-700 hover:underline"
        >
          <ExternalLink size={13} /> School website
        </a>
      )}
    </div>
  );
}
